import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, getStorageUrl } from '../../../../services/apiService';
import { useTenant } from '../../../../contexts/TenantContext';

const ThrowballNews = () => {
  const { siteSettings } = useTenant();
  const [pageData, setPageData] = useState({
    title: 'News',
    news_items: []
  });
  const [loading, setLoading] = useState(true);

  // Static/Default news items - shown when no dynamic data exists
  // No fallback articles. These were the founding federation's own news and
  // showed on any tenant that had published none.
  const staticNews = [];

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/public/site-settings');

      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        let newsItems = [];

        if (data.news_items && Array.isArray(data.news_items) && data.news_items.length > 0) {
          newsItems = data.news_items.map((item, index) => ({
            id: item.id || String(index + 1),
            title: item.title || '',
            excerpt: item.excerpt || '',
            content: item.content || '',
            image: getStorageUrl(item.image),
            certificate: item.certificate || null,
            date: item.date || ''
          }));
        }

        setPageData({
          title: data.news_title || 'News',
          news_items: newsItems.length > 0 ? newsItems : staticNews
        });
      } else {
        setPageData({
          title: 'News',
          news_items: staticNews
        });
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setPageData({
        title: 'News',
        news_items: staticNews
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Default placeholder image for news without images
  const defaultImage = 'https://picsum.photos/id/1015/600/400';

  return (
    <section className="tb-page-section tb-news-page-section">
      <div className="tb-container">
        {/* Top Banner */}
        <div className="tb-page-banner">
          <img src={siteSettings?.page_banners?.news || '/theme/assets/images/banner-4.jpg'} alt="News Banner" />
        </div>

        {/* Page Content */}
        <div className="tb-page-content">
          <div className="tb-heading-top">
            <h1 className="tb-page-title">{pageData.title}</h1>
            <span className="tb-mini-line"></span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading...</p>
            </div>
          ) : (
            <div className="tb-news-page-grid">
              {pageData.news_items.map((news) => (
                <div className="tb-news-page-card" key={news.id} style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  boxShadow: '0 2px 15px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}>
                  {/* News Image */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={news.image || defaultImage}
                      alt={news.title}
                      className="tb-news-page-card-img"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onError={(e) => {
                        e.target.src = defaultImage;
                      }}
                    />
                  </div>

                  {/* News Content */}
                  <div className="tb-news-page-card-content" style={{
                    padding: '20px'
                  }}>
                    <h3 className="tb-news-page-card-title" style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '10px',
                      color: '#333',
                      lineHeight: '1.4',
                      minHeight: '50px'
                    }}>{news.title}</h3>

                    <p className="tb-news-page-card-text" style={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      marginBottom: '15px',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>{news.excerpt}</p>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Link
                        to={`/news/${news.id}`}
                        className="tb-btn tb-btn-primary"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 20px',
                          backgroundColor: '#e74c3c',
                          color: '#fff',
                          textDecoration: 'none',
                          borderRadius: '5px',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.3s ease'
                        }}
                      >
                        Read More <i className="ri-arrow-right-line"></i>
                      </Link>
                      {news.certificate && (
                        <a
                          href={news.certificate}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            backgroundColor: '#065f46',
                            color: '#fff',
                            textDecoration: 'none',
                            borderRadius: '5px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'background-color 0.3s ease'
                          }}
                        >
                          <i className="ri-file-pdf-2-line"></i> View Certificate
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .tb-news-page-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 25px rgba(0,0,0,0.15);
        }
        .tb-news-page-card:hover .tb-news-page-card-img {
          transform: scale(1.05);
        }
        .tb-btn-primary:hover {
          background-color: #c0392b !important;
        }
        @media (max-width: 992px) {
          .tb-news-page-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 576px) {
          .tb-news-page-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ThrowballNews;
